<script lang="ts">
    let id = 0;
</script>

<script setup lang="ts">
    import { useField, useFieldError } from 'vee-validate';
    import { Field, FieldLabel, FieldDescription, FieldError } from '@/shared/ui/Field';
    import { InputGroup, InputGroupInput, InputGroupAddon, InputGroupButton } from '@/shared/ui/InputGroup';
    import { TooltipButton } from '@/shared/ui/TooltipButton';
    import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';

    const props = withDefaults(
        defineProps<{
            name?: string;
            label?: string;
            placeholder?: string;
            description?: string;
            rules?: string;
            disabled?: boolean;
        }>(),
        {
            name: () => `password_field_${++id}`,
        },
    );

    const model = defineModel<string>();
    const { meta, handleChange } = useField(props.name, props.rules, { initialValue: model.value });

    const error = useFieldError(props.name);
    const isInvalid = computed(() => meta.touched && !!error.value);

    const isRequired = computed(() => !!props.rules?.includes('required'));
    const isTooltipOpen = ref(false);

    const isRevealed = ref(false);
    const inputType = computed(() => (isRevealed.value ? 'text' : 'password'));

    watch(model, (value) => handleChange(value));
</script>

<template>
    <Field>
        <div class="flex gap-1 items-center">
            <FieldLabel v-if="label || $slots.label">
                <slot name="label">{{ label }}</slot>
            </FieldLabel>
            <TooltipButton
                v-if="$slots.tooltip"
                @click="isTooltipOpen = !isTooltipOpen"
            />
        </div>
        <div v-if="isTooltipOpen"><slot name="tooltip" /></div>
        <InputGroup>
            <InputGroupAddon v-if="$slots.addonBefore"><slot name="addonBefore" /></InputGroupAddon>
            <InputGroupInput
                v-model="model"
                :type="inputType"
                :name="name"
                :placeholder="placeholder"
                :disabled="disabled"
                :aria-invalid="isInvalid"
                :aria-required="isRequired"
            />
            <InputGroupAddon align="inline-end">
                <InputGroupButton
                    size="icon-sm"
                    type="button"
                    @click="isRevealed = !isRevealed"
                >
                    <EyeSlashIcon v-if="isRevealed" />
                    <EyeIcon v-else />
                </InputGroupButton>
            </InputGroupAddon>
        </InputGroup>

        <FieldDescription v-if="description || $slots.description">
            <slot name="description">{{ description }}</slot>
        </FieldDescription>

        <FieldError v-if="isInvalid">{{ error }}</FieldError>
    </Field>
</template>
